import {
  generateBrandModelVersionQueryParam,
  parseBrandModelVersionQueryParam,
  removeNullOrEmptyValues
} from "@/helpers/strings";
import PATHS from "@/constants/Paths";
import router from "@/plugins/router";
import {getAllOptions} from "@/services/api/ItemMenuSeeAll/SeeAllService";

/**
 * Converte objeto de parâmetros para query string da URL
 * @param {Object} state - Estado do store contendo params e configurações
 * @param {Object} fieldMap - Mapeamento de campos para a URL
 * @returns {string} Query string gerada
 */
export function objectToQueryParams(state, fieldMap) {
  const cities = state.params["cities"]
    ? state.params["cities"].map((city) => {
        const parts = city.split("-");
        if (parts.length >= 3) {
          const cityParts = parts.slice(0, parts.length - 2);
          const statePart = parts[parts.length - 2];
          const cityName = cityParts.join("-");
          const stateAbbrev = statePart.charAt(0);
          return cityName + "-" + stateAbbrev;
        }
        return city;
      })
    : null;

  const obj = {
    ...state.params,
    cities,
    fipe: state.fipe !== 0 ? state.fipe : undefined,
    priceMin: state.priceMin?.toString(),
    priceMax: state.priceMax?.toString(),
    anoMinimo: state.yearMin?.toString(),
    anoMaximo: state.yearMax?.toString(),
    kmMin: state.kmMin?.toString(),
    kmMax: state.kmMax?.toString(),
    order: state.orderBy?.toString(),
  };

  const params = generateBrandModelVersionQueryParam(obj, fieldMap);
  const hasOwnProperty = Object.prototype.hasOwnProperty;

  for (const key in obj) {
    if (hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      const mappedKey = fieldMap[key] || key;
      
      const shouldSkipBMV = ['brands', 'models', 'versions'].includes(key) && 
        Array.isArray(value) && value.length > 0 && 
        typeof value[0] === 'object' && value[0] !== null && 
        ('uuid' in value[0] || 'parentUuid' in value[0]);
        
      if (
        !shouldSkipBMV &&
        value != null &&
        value !== "" &&
        value !== undefined &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            if (
              typeof value[0] === "object" &&
              value[0] !== null &&
              "id" in value[0]
            ) {
              const formattedValues = value
                .map((item) => `${item.id}`)
                .join(",");
              params.push(`${mappedKey}=[${formattedValues}]`);
            } else {
              const formattedValues = value.join(",");
              params.push(`${mappedKey}=[${formattedValues}]`);
            }
          }
        } else {
          params.push(`${mappedKey}=${encodeURIComponent(value)}`);
        }
      }
    }
  }

  return params.join("&");
}

/**
 * Atualiza URL do browser com query params e gerencia navegação
 * @param {Object} state - Estado do store
 * @param {Object} fieldMap - Mapeamento de campos
 * @param {string} currentRoute - Nome da rota atual (opcional)
 */
export function updateUrlWithQueryParams(state, fieldMap, currentRoute) {
  const queryString = objectToQueryParams(state, fieldMap);
  
  try {
    const url = new URL(window.location.href);
    url.search = queryString;
    window.history.replaceState({}, '', url);
  } catch (error) {
    const fallbackUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${queryString}`;
    window.history.replaceState({}, '', fallbackUrl);
  }

  const routeName = currentRoute || router.currentRoute.value.name.toString();
  
  if(
    queryString && (
      routeName === "dashboardshowcase" ||
      routeName === "dashboard" ||
      routeName === "Favorites"
    )
  ) {
    const urlParams = new URLSearchParams(queryString);
    const campaignParam = urlParams.get('campaign');
    
    if (campaignParam) {
      const campaignId = campaignParam.replace(/[[\]]/g, '');
      urlParams.delete('campaign');
      
      const remainingParams = urlParams.toString();
      const campaignPath = `/campanhas/${campaignId}`;
      
      const url = new URL(window.location.href);
      url.pathname = campaignPath;
      url.search = remainingParams;
      window.location.href = url.toString();
    } else {
      const queryParams = {};
      if (queryString) {
        for (const [key, value] of urlParams) {
          queryParams[key] = value;
        }
      }

      const url = new URL(window.location.href);
      url.pathname = PATHS.Vehicles;
      url.search = queryString;
      window.location.href = url.toString();
    }
  }
}

/**
 * Limpa todos os filtros exceto os especificados
 * Usado principalmente quando o usuário troca de marca/categoria para resetar filtros dependentes
 * mantendo apenas o novo filtro selecionado
 * 
 * @param {Object} store - Store do Vuex
 * @param {string} setParamMutation - Nome da mutation para setParam
 * @param {Object} keepFilters - Filtros que devem ser mantidos (não serão resetados)
 * 
 * @example
 * // Ao trocar de marca, reseta todos exceto a nova marca
 * clearAllFiltersExcept(store, 'filterForm/setParam', { 
 *   brands: [{ id: 'honda' }] 
 * })
 * 
 * @example  
 * // Ao navegar por breadcrumb, mantém categoria e campanha
 * clearAllFiltersExcept(store, 'filterForm/setParam', { 
 *   category: [{ id: 'carros' }],
 *   campaign: 'carros-seminovos'
 * })
 */
export function clearAllFiltersExcept(store, setParamMutation, keepFilters = {}) {
  const allFilters = {
    brands: [], 
    models: [], 
    versions: [],
    cities: [], 
    location: [],
    fuel: [], 
    transmission: [], 
    optionals: [], 
    vehicleCondition: [], 
    reportStatus: [],
    quotation: [], 
    typeVehicle: [], 
    adType: [],
    priceMin: undefined, 
    priceMax: undefined, 
    fipe: 0,
    yearMin: undefined, 
    yearMax: undefined,
    kmMin: undefined, 
    kmMax: undefined,
    order: "", 
    campaign: undefined, 
    category: []
  };
  
  // Reseta apenas os filtros que NÃO devem ser mantidos
  for (const [filterName, defaultValue] of Object.entries(allFilters)) {
    const shouldKeep = Object.prototype.hasOwnProperty.call(keepFilters, filterName);
    
    if (!shouldKeep) {
      store.commit(setParamMutation, { [filterName]: defaultValue });
    }
  }

  // Aplica os filtros que devem ser mantidos
  for (const [filterName, value] of Object.entries(keepFilters)) {
    if (Object.prototype.hasOwnProperty.call(allFilters, filterName)) {
      store.commit(setParamMutation, { [filterName]: value });
    }
  }

  // Força atualização da URL
  store.commit(setParamMutation.replace('/setParam', '/objectToQueryParams'));
}

/**
 * Limpa todos os drawers do sistema
 * @param {Object} store - Store do Vuex
 */
export function clearAllDrawers(store) {
  const drawerKeys = [
    "key-vehicle-branding", "key-Modelos", "key-Versões",
    "key-Cidades", "key-Combustível", "key-Transmissão", 
    "key-Opcionais", "key-Condição", "key-Laudo", 
    "key-Modalidade", "key-Tipo"
  ];
  
  drawerKeys.forEach(key => {
    store.commit("drawer/clearSeeAllDrawerObjSelected", key);
  });
}

export async function createFormattedParams(
  rootState,
  state,
  commit,
  dispatch,
  fieldMap,
  params,
) {
  const newEntity = params?.entity || "";
  if (params === undefined || newEntity !== state.selectedEntity) {
    commit("setSelectedEntity", newEntity);
    if (
      state.optionalsData.optionals.length === 0
    ) {
      const [options] = await Promise.all([
        getAllOptions(),
      ]);

      state.optionalsData.optionals = options;
    }
    const url = new URL(window.location.href);
    const queryString = url.search;
    const urlParams = new URLSearchParams(queryString);
    const reverseFieldMap = Object.fromEntries(
      Object.entries(fieldMap).map(([key, value]) => [value, key])
    );
    const obj = {};

    for (const [i, value] of urlParams.entries()) {
      const key = i.replace(/\d/g, "");
      const originalKey = reverseFieldMap[key] || key;

      if (!["brands", "models", "versions"].includes(originalKey)) {
        if (value.startsWith("[") && value.endsWith("]")) {
          const trimmedValue = value.slice(1, -1);
          const values = trimmedValue.split(",");

          if (["optionals", "fuel", "transmission"].includes(originalKey)) {
            const newOptions = [];
            values.forEach((v) => {
              const optionExists = state.optionalsData[originalKey].find(
                (o) => o.id == v
              );
              if (optionExists) {
                newOptions.push({ id: v, title: optionExists.name });
              } else {
                newOptions.push(v);
              }
            });
            obj[originalKey] = newOptions;
          } else {
            obj[originalKey] = values;
          }
        } else {
          obj[originalKey] = value;
          if (originalKey.toString() === "fipe") {
            state.fipe = value;
          }
        }
      }
    }
    params = obj;

    const { brands, models, versions } = parseBrandModelVersionQueryParam(
      queryString,
      fieldMap
    );
    params = { ...params, brands, models, versions };

    params = removeNullOrEmptyValues(params);

    commit("setParamsObject", params);

    await dispatch("normalizeParams", params);

    if (params.order) {
      state.orderBy = params.order;
    }
  }

  params.slug = rootState.filterShowcase.slug ?? null;

  params.entity = router.currentRoute.value.params?.name;

  const paramsFormatted = { ...params };

  if (paramsFormatted.entity) {
    delete paramsFormatted.adType;
    delete paramsFormatted.fipe;
    delete paramsFormatted.order;
  }

  const keys = Object.keys(paramsFormatted);
  keys.forEach((key) => {
    paramsFormatted[key] =
      typeof paramsFormatted[key] === "object" && paramsFormatted[key]
        ? paramsFormatted[key]?.map((item) =>
          typeof item === "object" ? item?.value ?? item?.id : item
        )
        : paramsFormatted[key];
  });

  return paramsFormatted;
}